import { useEffect, useCallback } from 'react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { ChevronLeft, ChevronRight, ImageOff, X } from 'lucide-react'

import { type MosaicPhoto, resolvePhotoUrl } from '@/shared/domain'
import { env } from '@/shared/config/env'
import { cn } from '@/shared/ui/cn'
import { Button } from '@/shared/ui/primitives'

type PhotoViewerDialogProps = {
  photos: MosaicPhoto[]
  currentIndex: number | null
  onClose: () => void
  onNavigate: (direction: 'prev' | 'next') => void
}

function PhotoViewerDialog({
  photos,
  currentIndex,
  onClose,
  onNavigate,
}: PhotoViewerDialogProps) {
  const isOpen = currentIndex !== null
  const photo = isOpen ? photos[currentIndex] : null
  const fullSizeUrl = photo
    ? resolvePhotoUrl({ ...photo, thumbnailUrl: undefined }, env.supabaseUrl)
    : undefined
  const isFirst = currentIndex === 0
  const isLast = currentIndex === photos.length - 1

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (currentIndex === null) return
      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        onNavigate('prev')
      } else if (e.key === 'ArrowRight' && currentIndex < photos.length - 1) {
        onNavigate('next')
      }
    },
    [currentIndex, photos.length, onNavigate],
  )

  useEffect(() => {
    if (!isOpen) return
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleKeyDown])

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose()
  }

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/95 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-50 flex flex-col items-center justify-center outline-none"
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className="sr-only">
            Photo viewer
          </DialogPrimitive.Title>

          <div className="absolute right-4 top-4 z-10 flex items-center gap-4">
            <span className="text-sm text-white/70">
              {currentIndex !== null ? currentIndex + 1 : 0} of {photos.length}
            </span>
            <DialogPrimitive.Close asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-white/70 hover:bg-white/10 hover:text-white"
              >
                <X className="size-5" />
                <span className="sr-only">Close</span>
              </Button>
            </DialogPrimitive.Close>
          </div>

          <div className="relative flex w-full max-w-[95vw] flex-1 items-center justify-center">
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'absolute left-2 z-10 size-10 rounded-full text-white/70 hover:bg-white/10 hover:text-white',
                isFirst && 'pointer-events-none opacity-0',
              )}
              disabled={isFirst}
              onClick={() => onNavigate('prev')}
            >
              <ChevronLeft className="size-6" />
              <span className="sr-only">Previous photo</span>
            </Button>

            {photo ? (
              photo.mediaType === 'video' ? (
                <video
                  key={photo.id}
                  controls
                  playsInline
                  poster={resolvePhotoUrl(photo, env.supabaseUrl)}
                  src={resolvePhotoUrl({ ...photo, thumbnailUrl: undefined }, env.supabaseUrl)}
                  className="max-h-[80vh] max-w-[85vw] object-contain"
                />
              ) : fullSizeUrl ? (
                <img
                  src={fullSizeUrl}
                  alt={photo.caption ?? ''}
                  className="max-h-[80vh] max-w-[85vw] object-contain"
                />
              ) : (
                <div
                  aria-label="Photo unavailable"
                  className="flex size-64 flex-col items-center justify-center gap-2 rounded-lg bg-white/5 text-white/60"
                >
                  <ImageOff className="size-8" aria-hidden />
                  <span className="text-sm">Photo unavailable</span>
                </div>
              )
            ) : null}

            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'absolute right-2 z-10 size-10 rounded-full text-white/70 hover:bg-white/10 hover:text-white',
                isLast && 'pointer-events-none opacity-0',
              )}
              disabled={isLast}
              onClick={() => onNavigate('next')}
            >
              <ChevronRight className="size-6" />
              <span className="sr-only">Next photo</span>
            </Button>
          </div>

          {photo?.caption ? (
            <p className="absolute bottom-6 max-w-lg truncate px-4 text-center text-sm text-white/70">
              {photo.caption}
            </p>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export { PhotoViewerDialog }
export type { PhotoViewerDialogProps }
